import {
  CurrentUserProvider,
  QAccountAccessProvider,
} from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import {
  AsyncProviderConfig,
  asyncWithLDProvider,
} from 'launchdarkly-react-client-sdk';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { medtechApiClient } from './api';
import { userApi } from './api/user';
import { App } from './App';
import { LD_FLAGS_DEVELOPMENT } from './config/LDFlags';
import './i18n/config';
import {
  INDEX_UNMOUNT_EDITOR_ERROR,
  logCustomError,
} from './messages/LogErrorMessages';
import { getDocumentEditorGlobalWindowObject } from './util/AppUtils';
import { ErrorBoundary } from './views/ErrorBoundary';

const getWindow = () => window as any;

// Store roots for cleanup in MFE architecture
const roots = new Map<string, Root>();

getWindow().renderDocumentEditor = async (
  containerId: string,
  currentUser: CurrentUser | null,
) => {
  if (!currentUser) {
    currentUser = await userApi.fetchCurrentUser();
  }
  const launchDarklyContext: { [key: string]: string | number } = {
    kind: 'user',
    key: String(currentUser.companyId),
    loggedInUserId: String(currentUser.userId),
    createdTime: currentUser.company.createdTime,
    status: currentUser.company.status,
  };
  if (currentUser.company.pricingPlan) {
    launchDarklyContext['pricingPlan'] = currentUser.company.pricingPlan;
  }

  const ldProviderConfig: AsyncProviderConfig = {
    clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_KEY as string,
    context: launchDarklyContext,
    options: {},
  };

  if (import.meta.env.MODE === 'development') {
    ldProviderConfig.flags = LD_FLAGS_DEVELOPMENT;
  }

  const LDProvider = await asyncWithLDProvider(ldProviderConfig);

  const editorWindowData = getDocumentEditorGlobalWindowObject();

  editorWindowData.containerHtmlElement = document.getElementById(containerId);

  const queryClient = new QueryClient();

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container #${containerId} not found`);
    return;
  }

  const root = createRoot(container);
  roots.set(containerId, root);

  root.render(
    <ErrorBoundary>
      <LDProvider>
        <QueryClientProvider client={queryClient}>
          <CurrentUserProvider user={currentUser}>
            <QAccountAccessProvider mtbeClient={medtechApiClient}>
              <App currentUser={currentUser} />
            </QAccountAccessProvider>
          </CurrentUserProvider>
        </QueryClientProvider>
      </LDProvider>
    </ErrorBoundary>,
  );
};

getWindow().unmountDocumentEditor = (containerId: string) => {
  try {
    const root = roots.get(containerId);
    if (root) {
      root.unmount();
      roots.delete(containerId);
    }
  } catch (e) {
    logCustomError(INDEX_UNMOUNT_EDITOR_ERROR, { error: e });
    // even with the if statements its possible we have a race condition that is the if states the element exists BUT
    // then before we remove it the Application container has removed it
  }
  getDocumentEditorGlobalWindowObject().loaded = false;
};
